// components/Layout/EventGridLayout.jsx

import React, { useState, useEffect } from "react";
import ImageModal from "../Utils/ImageModal";
import { MapPin, Eye, User, Clock } from "lucide-react";
import "../Styles/EventGridLayout.css";

import {
  fetchEventData,
  fetchEventIdOnly,
  fetchSocietyIdEventId,
  fetchEventNameYear,
  fetchUserIdEventNameYear,
  fetchUserIdSocietyIdEventId,  // ✅ NEW import
  extractMediaByType,
} from "../Utils/fetchEventData";

// ==================== MAIN COMPONENT ====================

const EventGridLayout = ({ fetchMode, eventData, contentType }) => {
  const [gridState, setGridState] = useState({
    images:        [],
    formattedData: null,
    loading:       true,
    error:         null,
  });
  const [selectedImage, setSelectedImage] = useState(null);

  // ==================== FETCH BASED ON MODE ====================

  useEffect(() => {
    if (!eventData || fetchMode === "unknown") {
      setGridState({
        images:        [],
        formattedData: null,
        loading:       false,
        error:         "No valid event data.",
      });
      return;
    }

    let cancelled = false;

    console.log("eventData: ", eventData); 

    const loadData = async () => {
      try {
        setGridState((prev) => ({ ...prev, loading: true, error: null }));

        let formattedData = null;

        switch (fetchMode) {
          case "userid-eventid":
            formattedData = await fetchEventData(
              eventData.eventId,
              eventData.year
            );
            break;

          case "eventid-only":
            formattedData = await fetchEventIdOnly(
              eventData.eventId
            );
            break;

          case "societyid-eventid":
            formattedData = await fetchSocietyIdEventId(
              eventData.societyId,
              eventData.eventId
            );
            break;

          case "eventname-year":
            formattedData = await fetchEventNameYear(
              eventData.eventName,
              eventData.year
            );
            break;

          case "userid-eventname-year":
            formattedData = await fetchUserIdEventNameYear(
              eventData.userId,
              eventData.eventName,
              eventData.year
            );
            break;

          // ✅ NEW case
          case "userid-societyid-eventid":
            formattedData = await fetchUserIdSocietyIdEventId(
              eventData.userId,
              eventData.societyId,
              eventData.eventId
            );
            break;

          default:
            throw new Error(`Unknown fetch mode: ${fetchMode}`);
        }

        if (!formattedData) {
          throw new Error(`No data returned for mode: ${fetchMode}`);
        }

        const images = extractMediaByType(formattedData, contentType);

        if (!cancelled) {
          setGridState({ images, formattedData, loading: false, error: null });
        }

      } catch (err) {
        if (!cancelled) {
          const message = err instanceof Error ? err.message : "Failed to load data";
          console.error("[EventGrid] Error:", message);
          setGridState({
            images:        [],
            formattedData: null,
            loading:       false,
            error:         message,
          });
        }
      }
    };

    loadData();

    return () => { cancelled = true; };

  }, [fetchMode, eventData, contentType]);

  // ==================== HANDLERS ====================

  const handleImageClick = (image) => setSelectedImage(image);
  const handleCloseModal = ()      => setSelectedImage(null);

  if (gridState.loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner" />
        <p>Loading {contentType}...</p>
      </div>
    );
  }

  if (gridState.error) {
    return <p className="error-message">{gridState.error}</p>;
  }

  const fd = gridState.formattedData;

  return (
    <div className="ep-grid-container" style={{background: "rgba(36,36,45,0.2)"}}>

      <div className="ep-event-info">
        <h2 className="ep-event-type">
          {fd?.eventName || eventData?.eventName}
        </h2>
        {fd?.createdOn && (
          <p className="ep-event-date">
            <Clock size={16} />
            {new Date(fd.createdOn).toLocaleString("en-US", {
              year:   "numeric",
              month:  "long",
              day:    "numeric",
              hour:   "2-digit",
              minute: "2-digit",
            })}
          </p>
        )}
      </div>

      <div className="ep-grid-layout" style={{background: "black", borderRadius: 10}}>
        {gridState.images.length === 0 ? (
          <p className="no-content-message">
            No {contentType} available for this event
          </p>
        ) : (
          gridState.images.map((image, index) => (
            <div
              key={image.id || index}
              className="ep-image-card"
              onClick={() => handleImageClick(image)}
              role="button"
              tabIndex={0}
              aria-label={`View ${image.description || "image"}`}
              onKeyPress={(e) => { if (e.key === "Enter") handleImageClick(image); }}
              style={{background: "rgba(36,36,45,0.2)", border: "1px solid darkslategray"}}
            >
              <div className="ep-image-container">
                <img
                  src={image.imageurl || "https://placehold.co/400x300?text=No+Image"}
                  alt={image.description || "Event image"}
                  className="ep-image"
                  loading="lazy"
                />
              </div>

              <div className="ep-card-info" style={{color: "white"}}>
                <div className="ep-user-info">
                  <div className="ep-user-avatar">
                    {image.user_avatar ? (
                      <img src={image.user_avatar} alt={image.posted_by || "User"} />
                    ) : (
                      <User size={20} />
                    )}
                  </div>
                  <div className="ep-user-details">
                    <p className="ep-username" style={{color: "white"}} >{image.posted_by || "Anonymous"}</p>
                    <p className="ep-post-time" style={{color: "white"}}>
                      {image.timestamp
                        ? new Date(image.timestamp).toLocaleDateString()
                        : "Recently"}
                    </p>
                  </div>
                </div>

                <div className="ep-card-meta">
                  {image.location && (
                    <div className="ep-meta-item">
                      <MapPin size={14} style={{color: "white"}} />
                      <span style={{color: "white"}}>{image.location}</span>
                    </div>
                  )}
                  <div className="ep-meta-item">
                    <Eye size={14} style={{color: "white"}} />
                    <span style={{color: "white"}}>{image.impressions || 0} views</span>
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {selectedImage && (
        <ImageModal image={selectedImage} onClose={handleCloseModal} />
      )}
    </div>
  );
};

export default EventGridLayout;
